import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const Home: React.FC = () => {
    const { firebaseUser, dbUser } = useAuth();

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary-50 to-green-100">
            {/* Hero Section */}
            <div className="relative overflow-hidden">
                <div className="max-w-7xl mx-auto">
                    <div className="relative z-10 pb-8 bg-gradient-to-br from-primary-50 to-green-100 sm:pb-16 md:pb-20 lg:max-w-2xl lg:w-full lg:pb-28 xl:pb-32">
                        <main className="mt-10 mx-auto max-w-7xl px-4 sm:mt-12 sm:px-6 md:mt-16 lg:mt-20 lg:px-8 xl:mt-28">
                            <div className="sm:text-center lg:text-left">
                                <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
                                    <span className="block xl:inline">
                                        Plan your meals
                                    </span>{" "}
                                    <span className="block text-primary-600 xl:inline">
                                        effortlessly
                                    </span>
                                </h1>
                                <p className="mt-3 text-base text-gray-500 sm:mt-5 sm:text-lg sm:max-w-xl sm:mx-auto md:mt-5 md:text-xl lg:mx-0">
                                    Create personalized meal plans, track
                                    nutrition, and simplify your weekly cooking.
                                    Perfect for busy professionals and
                                    health-conscious individuals.
                                </p>

                                {/* CTA Buttons */}
                                <div className="mt-5 sm:mt-8 sm:flex sm:justify-center lg:justify-start">
                                    {firebaseUser ? (
                                        <div className="rounded-md shadow">
                                            <Link
                                                to="/profile"
                                                className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 md:py-4 md:text-lg md:px-10"
                                            >
                                                Go to Profile
                                            </Link>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="rounded-md shadow">
                                                <Link
                                                    to="/signup"
                                                    className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 md:py-4 md:text-lg md:px-10"
                                                >
                                                    Get started
                                                </Link>
                                            </div>
                                            <div className="mt-3 sm:mt-0 sm:ml-3">
                                                <Link
                                                    to="/signin"
                                                    className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 md:py-4 md:text-lg md:px-10"
                                                >
                                                    Sign in
                                                </Link>
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* User welcome message */}
                                {firebaseUser && dbUser && (
                                    <div className="mt-6 text-center lg:text-left">
                                        <p className="text-lg text-gray-700">
                                            Welcome back,{" "}
                                            <span className="font-semibold text-primary-600">
                                                {dbUser.first_name}
                                            </span>
                                            !
                                        </p>
                                        <p className="text-sm text-gray-500 mt-1">
                                            Subscription:{" "}
                                            <span className="capitalize font-medium">
                                                {dbUser.subscription_status}
                                            </span>
                                            {dbUser.is_premium && (
                                                <span className="ml-2 text-primary-600">
                                                    ✨ Premium
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </main>
                    </div>
                </div>

                {/* Hero Media */}
                <div className="lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2 flex items-center justify-center">
                    <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-primary-100 to-green-50 p-6 sm:p-8 rounded-lg shadow-lg">
                        <div className="w-full max-w-2xl lg:max-w-xl xl:max-w-2xl">
                            <div className="mb-4 text-center">
                                <h3 className="text-xl font-semibold text-gray-900">See MealPlan in action</h3>
                                <p className="mt-1 text-sm text-gray-600">A quick walkthrough of planning, editing, and nutrition insights.</p>
                            </div>
                            <div className="relative aspect-video overflow-hidden rounded-xl shadow-2xl ring-1 ring-black/10 bg-black">
                                <video
                                    className="absolute inset-0 w-full h-full object-contain"
                                    src="/videos/MealPlanDemo.mp4"
                                    controls
                                    playsInline
                                    poster="/mealplan.png"
                                />
                            </div>
                            <div className="mt-3 flex items-center justify-center">
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/80 backdrop-blur ring-1 ring-black/10 text-xs text-gray-700">
                                    🎬 Quick tour of MealPlan features
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Features Section */}
            <div className="py-12 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="lg:text-center">
                        <h2 className="text-base text-primary-600 font-semibold tracking-wide uppercase">
                            Features
                        </h2>
                        <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                            Everything you need for meal planning
                        </p>
                    </div>

                    <div className="mt-10">
                        <div className="space-y-10 md:space-y-0 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-10">
                            {/* Feature 1 */}
                            <div className="relative">
                                <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-primary-500 text-white">
                                    🍽️
                                </div>
                                <p className="ml-16 text-lg leading-6 font-medium text-gray-900">
                                    Personalized Meal Plans
                                </p>
                                <p className="mt-2 ml-16 text-base text-gray-500">
                                    Create custom meal plans based on your
                                    dietary preferences and nutritional goals.
                                </p>
                            </div>

                            {/* Feature 2 */}
                            <div className="relative">
                                <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-primary-500 text-white">
                                    📊
                                </div>
                                <p className="ml-16 text-lg leading-6 font-medium text-gray-900">
                                    Nutrition Tracking
                                </p>
                                <p className="mt-2 ml-16 text-base text-gray-500">
                                    Monitor your macros and micronutrients with
                                    detailed nutritional analysis.
                                </p>
                            </div>

                            {/* Feature 3 */}
                            <div className="relative">
                                <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-primary-500 text-white">
                                    🛒
                                </div>
                                <p className="ml-16 text-lg leading-6 font-medium text-gray-900">
                                    Smart Shopping Lists
                                </p>
                                <p className="mt-2 ml-16 text-base text-gray-500">
                                    Generate organized shopping lists
                                    automatically from your meal plans.
                                </p>
                            </div>

                            {/* Feature 4 */}
                            <div className="relative">
                                <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-primary-500 text-white">
                                    ⭐
                                </div>
                                <p className="ml-16 text-lg leading-6 font-medium text-gray-900">
                                    Premium Features
                                </p>
                                <p className="mt-2 ml-16 text-base text-gray-500">
                                    Advanced recipes, meal prep guides, and
                                    personalized nutrition coaching.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            {!firebaseUser && (
                <div className="bg-primary-50">
                    <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
                        <h2 className="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                            <span className="block">Ready to get started?</span>
                            <span className="block text-primary-600">
                                Create your account today.
                            </span>
                        </h2>
                        <div className="mt-8 flex lg:mt-0 lg:flex-shrink-0">
                            <div className="inline-flex rounded-md shadow">
                                <Link
                                    to="/signup"
                                    className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                                >
                                    Get started
                                </Link>
                            </div>
                            <div className="ml-3 inline-flex rounded-md shadow">
                                <Link
                                    to="/about"
                                    className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-primary-600 bg-white hover:bg-primary-50"
                                >
                                    Learn more
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Home;
